/** Read featured / new / sale flags from API product (camelCase or snake_case). */
export function readProductStatusFlags(product) {
  if (!product || typeof product !== 'object') {
    return { isFeatured: false, isNew: false, isOnSale: false };
  }
  const isFeatured = Boolean(
    product.isFeatured ?? product.is_featured ?? product.featured
  );
  const isNew = Boolean(
    product.isNew ??
      product.is_new ??
      product.newArrival ??
      product.isNewArrival ??
      product.new_arrival
  );
  const isOnSale = Boolean(
    product.isOnSale ?? product.is_on_sale ?? product.onSale ?? product.on_sale
  );
  return { isFeatured, isNew, isOnSale };
}

/** Payload for PUT /admin/products/status/:id after multipart create. */
export function buildProductStatusSyncPayload(formProduct, { includeFeatured = true } = {}) {
  if (!formProduct) {
    return { isFeatured: false, isNew: false, isOnSale: false };
  }
  return {
    isFeatured: includeFeatured ? Boolean(formProduct.isFeatured) : false,
    isNew: Boolean(formProduct.isNew),
    isOnSale: Boolean(formProduct.isOnSale),
  };
}

/** One flag — same request shape as Products list manual toggle. */
export async function putProductStatusFlag(productId, partial, { getApiUrl, getAdminToken }) {
  const id = String(productId || '').trim();
  if (!id) throw new Error('Missing product ID for status update');
  const token = getAdminToken();
  if (!token) throw new Error('No admin token found');

  const response = await fetch(getApiUrl(`/admin/products/status/${id}`), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(partial),
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.message || result.error || 'Failed to update product status');
  }
  return result;
}

/**
 * Apply Featured / New / Sale after create or full save.
 * Runs each flag in its own PUT (batch + earlier pricing/stock calls can wipe flags on some backends).
 */
export async function syncProductStatusFlagsAfterSave(
  productId,
  formProduct,
  { getApiUrl, getAdminToken },
  { includeFeatured = true } = {}
) {
  const id = String(productId || '').trim();
  if (!id) return null;

  const flags = buildProductStatusSyncPayload(formProduct, { includeFeatured });

  if (includeFeatured) {
    await putProductStatusFlag(id, { isFeatured: flags.isFeatured }, { getApiUrl, getAdminToken });
  }
  await putProductStatusFlag(id, { isNew: flags.isNew }, { getApiUrl, getAdminToken });
  await putProductStatusFlag(id, { isOnSale: flags.isOnSale }, { getApiUrl, getAdminToken });

  return flags;
}
